<template>
    <div
        class="hts-form-input form-control"
        :class="{ disabled: disabled, readonly: readonly }"
        @click="onClick"
        @focus="onFocus">
        <label
            v-if="label"
            class="label"
            :for="name">
            <span class="label-text">
                {{ label }}<span
                    v-if="required"
                    class="text-error-500 ml-1">
                    *
                </span>
            </span>
        </label>
        <div class="flex">
            <div
                v-if="hasPrefixInput"
                class="mr-2">
                <slot name="prefixInput" />
            </div>
            <div
                class="flex relative w-full"
                :class="{ ['hts-form-input__' + size]: size }">
                <span
                    v-if="hasPrefix"
                    class="prefix">
                    <slot name="prefix" />
                </span>
                <input
                    :id="name"
                    ref="input"
                    class="z-20 input shadow-none input-bordered w-full p-2"
                    :class="{ ...cColorSize, ...cInputClass }"
                    :name="prop"
                    :autocomplete="autocomplete"
                    :disabled="locked"
                    :placeholder="placeholder"
                    :readonly="readonly"
                    :required="required"
                    :type="cType"
                    :value="cTempValue"
                    @input="onInput"
                    @paste="onPaste"
                    @blur="onBlur" >
                <span
                    v-if="hasSuffix"
                    class="suffix">
                    <hts-tooltip
                        v-if="disabled"
                        :content="contentTooltipDisabled"
                        class="flex px-1 items-center">
                        <template #reference>
                            <hi-lock-closed-icon class="flex items-center w-5 h-5" />
                        </template>
                    </hts-tooltip>
                    <slot name="suffix" />
                </span>
                <slot name="custom" />
            </div>
        </div>
        <label
            v-if="errorMessage || description"
            class="label">
            <span
                v-if="errorMessage"
                class="hts-form-input__error">
                {{ errorMessage }}
            </span>
            <span
                v-if="description"
                class="hts-form-input__description">
                {{ description }}
            </span>
        </label>
    </div>
</template>

<script lang="ts" setup>
import { useField, useFieldError } from 'vee-validate'
import { daisyuiColorSize } from '~/components/utils/daisyui'
import { $lodash } from '#imports'

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: ''
  },
  label: String,
  placeholder: String,
  description: String,
  prop: String,
  name: String,
  rules: Object,
  type: {
    type: String,
    default: 'text'
  },
  contentTooltipDisabled: {
    type: String,
    default: 'Input is disabled'
  },
  size: String,
  color: String as PropType<string | undefined | null>,
  min: Number,
  max: Number,
  required: Boolean,
  disabled: Boolean,
  readonly: Boolean,
  autocomplete: {
    type: String,
    default: ''
  },
  defaultValue: String,
  roundedNone: Boolean,
  hasPrefixInput: {
    type: Boolean,
    default: false
  },
  numberDecimal: Number,
  numberInterger: {
    type: Boolean,
    default: false
  }
})
const slots = useSlots()
const { modelValue, type, prop, name, rules, min, max, disabled, required, readonly, roundedNone, numberDecimal, numberInterger } = toRefs(props)

const { $formatters } = useNuxtApp()
const locked = computed(() => readonly.value || disabled.value)
const hasPrefix = computed(() => !!slots.prefix)
const hasSuffix = computed(() => !!slots.suffix || disabled.value)
const cColorSize = computed(() => daisyuiColorSize('input', props))
const cInputClass = computed(() => {
  return {
    'rounded-r-lg rounded-l-none': hasPrefix.value && !hasSuffix.value,
    'rounded-l-lg rounded-r-none': hasSuffix.value && !hasPrefix.value,
    'rounded-none': roundedNone.value || (hasPrefix.value && hasSuffix.value),
    disabled: locked.value
    // 'bg-gray-200': (readonly.value || disabled.value) &&
  }
})

const CNumberInterger = computed(() => {
  if (numberInterger.value) {
    return 0
  }

  if (typeof numberInterger.value === 'number' && numberInterger.value >= 0) {
    return numberInterger.value
  }

  return 8
})

const emits = defineEmits(['update:modelValue', 'change', 'click', 'focus', 'blur'])
const refInput = templateRef<HTMLInputElement | null>('input', null)

const cType = computed(() => (['text', 'password'].includes(type.value) ? type.value : 'text'))
const cValue = computed<string | number>({
  get: () => {
    if (modelValue === undefined || modelValue === null || typeof modelValue !== 'object') {
      return ''
    }
    if (typeof modelValue.value !== 'string' && typeof modelValue.value !== 'number') {
      return ''
    }
    return modelValue.value
  },
  set: (val) => {
    field?.setValue(val)
    emits('update:modelValue', val)
    emitOnChanged(val)
  }
})

const emitOnChanged = $lodash.debounce((val) => {
  emits('change', val)
}, 200)

const cTempValue = computed({
  get: () => {
    if (typeof cValue.value !== 'string' && typeof cValue.value !== 'number') {
      cValue.value = ''
    }
    if (type.value === 'number') {
      if (typeof cValue.value === 'number') {
        return $formatters.formatCurrency(parseFloat(cValue.value.toString()), CNumberInterger.value)
      }
      return cValue.value
    }
    return cValue.value
  },
  set: (val) => {
    if (type.value === 'number' && val) {
      cValue.value = parseFloat(val)
    } else {
      cValue.value = val
    }
  }
})

let field: any
let errorMessage: undefined | ComputedRef
const cProp = computed(() => prop?.value)
const setFields = (prop: any) => {
  if (typeof prop === 'string' && prop !== '') {
    field = useField(prop, undefined, {
      label: name?.value || prop,
      validateOnValueUpdate: true,
      validateOnMount: false
    })
    errorMessage = useFieldError(prop)
  }
}
onMounted(() => {
  setFields(cProp.value)
  nextTick(() => {
    if (modelValue !== undefined && modelValue.value !== null && modelValue.value !== '') {
      field?.setValue(modelValue.value)
    }
  })
  if (modelValue?.value === undefined) {
    emits('update:modelValue', '')
  }
})
watch(
  () => modelValue.value,
  (val: string | number | any[] | undefined | null) => {
    field?.setValue(val)
  }
)

const onInput = (e: any) => {
  if (!(refInput && refInput.value)) {
    return
  }

  const val = refInput.value.value
  if (!disabled.value && !readonly.value) {
    if (type.value === 'number') {
      const dotCount = (val.match(/\./g) || []).length

      if (dotCount > 1) {
        refInput.value.value = cTempValue.value
        return e.preventDefault()
      }

      const validText = val.replace(/[^0-9,.-]+/g, '')
      refInput.value.value = validText
      if (!validText) {
        cTempValue.value = null
        return
      }
      if (validText.indexOf('.') === validText.length - 1 || validText === '-') {
        return
      }
      const realVal = Number(validText.replace(/[^0-9.-]+/g, ''))

      if (isNaN(realVal)) {
        cTempValue.value = ''
        return
      }
      let temp = realVal
      if (min?.value !== undefined && realVal < min.value) {
        temp = min.value
      } else if (max?.value !== undefined && realVal > max.value) {
        temp = max?.value
      }

      if (dotCount && CNumberInterger.value === 0) {
        temp = Math.floor(temp)
        refInput.value.value = $formatters.formatCurrency(temp, CNumberInterger.value)
      }

      cTempValue.value = temp
    } else if (max?.value !== undefined && val.length > max.value) {
      cTempValue.value = val.substring(0, max.value)
      refInput.value.value = cTempValue.value
    } else {
      cTempValue.value = val
    }
  }
}
const onPaste = (e: any) => {
  if (type.value === 'number') {
    e.preventDefault()
    const pasteData = e.clipboardData.getData('text')
    const value = Number(pasteData.replace(/[^0-9.-]+/g, ''))
    const dotCount = (pasteData.match(/\./g) || []).length

    if (dotCount > 2) {
      return
    }

    if (numberInterger.value) {
      const realVal = Number(pasteData.replace(/[^0-9-]+/g, ''))
      if (!isNaN(realVal)) {
        cTempValue.value = realVal
      }
      return
    }

    if (!isNaN(value)) {
      cTempValue.value = value
    }
  }
}

const onClick = ($event: any) => {
  emits('click', $event)
}
const onFocus = ($event: any) => {
  emits('focus', $event)
}
const onBlur = ($event: any) => {
  if (type.value === 'number' && typeof modelValue.value === 'number') {
    if (min?.value !== undefined && modelValue.value < min.value) {
      cTempValue.value = min.value
    } else if (max?.value !== undefined && modelValue.value > max.value) {
      cTempValue.value = max?.value
    }
  }
  emits('blur', $event)
}
const onChange = ($event: any) => {
  emits('change', $event)
}
</script>
